import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import Container from '../ui/Container';
import SectionHeading from '../ui/SectionHeading';
import CoreValueModal from './CoreValueModal';

const values = [
  {
    title: "Godliness & Integrity",
    shortDescription: "Upholding the highest standards of integrity in everything we do.",
    fullDescription: "At BNC Africa Holdings, we are committed to upholding the highest standards of integrity in everything we do. Guided by principles of honesty, fairness, and accountability, we ensure that our actions and decisions are always aligned with ethical practices. Our foundation in godliness informs our conduct, driving us to be responsible stewards in every aspect of our business, creating lasting trust with our stakeholders. We believe that doing the right thing, even when no one is watching, is essential for building a strong and credible foundation for growth across all our ventures."
  },
  {
    title: "Hard Work & Excellence",
    shortDescription: "Driven by a relentless pursuit of quality and outstanding results.",
    fullDescription: "We believe that excellence is achieved through consistent effort, determination, and a passion for continuous improvement. At BNC Africa Holdings, we place immense value on hard work as the cornerstone of success. Our teams are driven by a relentless pursuit of quality, ensuring that we deliver outstanding results in every project and service. Excellence is not just a goal, but a standard embedded in our culture—one that challenges us to surpass expectations, elevate our performance, and deliver impactful solutions that transform lives."
  },
  {
    title: "Empowerment & Sustainability",
    shortDescription: "Providing tools and resources to help people reach their full potential.",
    fullDescription: "Empowering individuals, communities, and businesses is at the heart of our mission. We aim to provide the tools, resources, and opportunities needed to help people reach their full potential, fostering an environment where innovation thrives. At the same time, we are deeply committed to sustainability—ensuring that the solutions we create are not only effective today but can be maintained for future generations. Through our work, we strive to contribute to long-term social and environmental sustainability, making a positive impact that goes beyond short-term successes."
  },
  {
    title: "Collaboration & People-Centric Approach",
    shortDescription: "Working together with partners to drive meaningful change.",
    fullDescription: "We believe that collaboration is the key to driving meaningful change. By working together with our partners, employees, and communities, we are able to harness diverse perspectives and unlock new opportunities for growth. Our people-centric approach ensures that every decision we make is focused on creating value for the people we serve—whether it's providing our employees with a supportive and empowering work environment, delivering customer-centric services, or building relationships that benefit all stakeholders. At BNC Africa Holdings, people come first, and collaboration is essential to achieving shared success."
  },
  {
    title: "Innovation & Transparency",
    shortDescription: "Embracing creativity and maintaining clear communication.",
    fullDescription: "Innovation drives our ability to lead in a rapidly changing world, and at BNC Africa Holdings, we are committed to embracing creativity, technology, and new ideas to stay ahead of the curve. We strive to foster an environment where bold thinking and innovation are encouraged and celebrated. Transparency is equally important to us; we believe in being open, honest, and accountable in all our dealings. By maintaining clear communication and transparent practices, we build trust with our stakeholders and ensure that our actions align with our values and mission."
  }
];

export default function CoreValues() {
  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  return (
    <section className="bg-brown-50 py-16">
      <Container>
        <SectionHeading
          title="Our Core Values"
          subtitle="Our core values define who we are at BNC Africa Holdings, inspiring every decision and action as we work towards building a brighter, more sustainable future."
          centered
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedValue(index)}
            >
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.shortDescription}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <CoreValueModal
          isOpen={selectedValue !== null}
          onClose={() => setSelectedValue(null)}
          value={selectedValue !== null ? values[selectedValue] : null}
        />
      </Container>
    </section>
  );
}